// src/components/Home.js
import React, { useState, useEffect, useRef, useContext } from 'react';
import axiosInstance from '../axiosConfig';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';

import '../Chess.css';

// Utility functions for getting/clearing cookies
function getCookie(name) {
    let cookieValue = '';
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            // Does this cookie string begin with the name we want?
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
}

function clearCookie(name) {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

const Chess = () => {
    //const [active, setActive] = useState([]);
    const { user } = useContext(AuthContext);
    const [isLoading, setIsLoading] = useState(true); // Loading state
    const [error, setError] = useState('');
    const userId = user?.user_id;
    const navigate = useNavigate();
    const [username, setUsername] = useState([]);

    //active list and history list
    const [active, setActive] = useState([]);
    const [history, setHistory] = useState([]);
    //socket
    const [isPSConnected, setIsPSConnected] = useState(false);
    const pollSocket = useRef(null);
    
    const handleUpdate = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/api/v1/update-chess/');
            setActive(response.data["active"])
            setHistory(response.data["history"])
            setError('');
        } catch (error) {
            if (error.response) {
                if (error.response.status === 401 || error.response.status === 403) {
                    navigate('/login');
                }
                else {
                    setError('Error fetching active and history.');
                    console.error('Error fetching active and history:', error);
                }
            }       
            else {
                setError('Network error. Please try again later.');
                console.error('Network Error: Please try again later.', error);
            }
        } finally {
            console.log("Successful load")
            setIsLoading(false);
        }
    }
    useEffect(() => {
        // Dynamically determine the WebSocket protocol
        const wsScheme = window.location.protocol === "https:" ? "wss" : "ws";
        pollSocket.current = new WebSocket(
            wsScheme + '://' + window.location.host + '/ws/pollPlayers/' + userId + '/'
        );
        // New Websocket Connection
        pollSocket.current.onopen = (e) => {
            console.log("PollSocket connection established.");
            pollSocket.current.send(JSON.stringify({
                'action': "login",
                'current_user': userId
            }));
            setIsPSConnected(true)
            // Enable the send button or perform any actions that require an open connection
        }

        //Handles responses from WebSocket
        pollSocket.current.onmessage = (e) => {
            const data = JSON.parse(e.data);
            let action = data["action"]
            if(action === "login"){
                setUsername(data["username"])
            }
            //if a newGame is just created, or user disconnects without resign, redirect to chess
            else if(action === "newGame" || action === "continue"){
                const userrole = data["role"]
                console.log(username + " navigating to game")
                navigate(data["url"]+ data["match_id"]+"/" + data["user_id"] + "/", { state: { username: username, role: userrole } });
            }
            //if user not in game, socket updates the list of available players and history
            //as players log in
            else if(action === "update"){
                handleUpdate()
            }
        }
        pollSocket.current.onerror = (e) =>  {
            console.error("PollSocket error observed:", e);
        };
        //Close connection.
        pollSocket.current.onclose = (e) =>  {
            console.log("PollSocket connection closed.");
        };
        return () => {
            console.log('[PollSocket] Cleanup: Closing socket...');
            if (pollSocket.current) {
            pollSocket.current.close();
            }
        };

        
    }, []);
    //send challenge request to the pollConsumer WebSocket
        function challenge(e, target_uname) {
            if (pollSocket.current && isPSConnected) {
                pollSocket.current.send(JSON.stringify({
                    'action': "challenge",
                    'target_name': target_uname,
                    'status': "available",
                }));
            }
            else {
                console.error("WebSocket is not open.");
                alert("WebSocket connection is not open. Please try again later.");
            }
        };

        //send accept request to the pollConsumer WebSocket
        function accept(e, target_uname) {
            if (pollSocket.current && isPSConnected) {
                pollSocket.current.send(JSON.stringify({
                    'action': "accept",
                    'target_name': target_uname,
                    'status': "unavailable"
                }));
            }
            else {
                console.error("WebSocket is not open.");
                alert("WebSocket connection is not open. Please try again later.");
            }
        };
        //send reject request to the pollConsumer WebSocket
        function reject(e, target_uname) {
            if (pollSocket.current && isPSConnected) {
                pollSocket.current.send(JSON.stringify({
                    'action': "reject",
                    'target_name': target_uname,
                    'status': "available"
                }));
            }
            else {
                console.error("WebSocket is not open.");
                alert("WebSocket connection is not open. Please try again later.");
            }
        };
    //create table of available players to challenge
    function createAvailable(){
        return (
            <table>
              <tr>
                <th id="hUsers">
                    User
                </th>
                <th id="hButtons"></th>
              </tr>
              {Object.values(active).map((entry, idx) => (
                <tr key={idx}>
                  <td>{entry.target_name}:</td> 
                  {entry.status === "available" ? (
                    <td>
                      <button type="button" 
                        onClick={(e) => challenge(e, entry.target_name)}
                        className="btn btn-primary" 
                      >challenge</button>
                    </td>
                  ) : (
                    <td>
                      <button type="button" 
                        onClick={(e) => accept(e, entry.target_name)}
                        className="btn btn-primary" 
                      >accept</button>
                      <button type="button" 
                        onClick={(e) => reject(e, entry.target_name)}
                        className="btn btn-primary" 
                      >reject</button>
                    </td>
                    )}    
                </tr>
              ))}
            </table>
        )
    }
    //create table of game history that belongs to the logged in player
    function createHistory(){
        return (
            <table>
              <tr>
                <th id="hGame">Game</th>
                <th id="hPlayer">Opponent</th>
                <th id="hWinner">Winner</th>
              </tr>
              {Object.entries(history).map(([match_id, entry]) => (
                <tr key={match_id}>
                  <td>{match_id}</td> 
                  <td>{entry.target_name}</td>
                  <td>{entry.winner}</td>    
                </tr>
              ))}
            </table>
        )
    }
    const loadChess = () => {
        return (
            <div className="row">
              <div className="col-6" id="half1">
                <h2>Challenge</h2>
                <div id="utable">
                  {error && <p className="text-danger text-center">{error}</p>}
                  {createAvailable()}
                </div>
              </div>
              <div className="col-6" id="half2">
                <h2>History</h2>
                <div id="htable">
                  {error && <p className="text-danger text-center">{error}</p>}
                  {createHistory()}
                </div>
                </div>
            </div>
        )
    }
    // -------------------------------------------
    // RENDER
    // -------------------------------------------
    return (
        <div className="container">
          <h1>Chess</h1>
          {loadChess()}
        </div>
    );
}

export default Chess;




